<template>
    <div class="explain-page-control">
        <button
            ref="triggerButton"
            type="button"
            class="explain-page-trigger"
            aria-haspopup="dialog"
            @click="openGuide"
        >
            <i class="material-icons" aria-hidden="true">help_outline</i>
            <span>Explain page</span>
        </button>

        <Teleport to="body">
            <div
                v-if="isOpen"
                class="explain-page-backdrop"
                @click.self="closeGuide"
            >
                <section
                    class="explain-page-dialog"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="explain-page-title"
                    aria-describedby="explain-page-intro"
                    tabindex="-1"
                    @keydown.esc="closeGuide"
                >
                    <header class="explain-page-header">
                        <div class="explain-page-mark" aria-hidden="true">
                            <i class="material-icons">menu_book</i>
                        </div>
                        <div class="explain-page-heading">
                            <span class="explain-page-eyebrow">PAGE GUIDE</span>
                            <h2 id="explain-page-title">{{ guide.title }}</h2>
                        </div>
                        <button
                            ref="closeButton"
                            type="button"
                            class="explain-page-close"
                            aria-label="Close page explanation"
                            @click="closeGuide"
                        >
                            <i class="material-icons" aria-hidden="true">close</i>
                        </button>
                    </header>

                    <div class="explain-page-body">
                        <p id="explain-page-intro" class="explain-page-intro">{{ guide.intro }}</p>

                        <section v-if="fieldGuides.length" class="explain-guide-section">
                            <h3>Fields on this page</h3>
                            <div class="explain-field-list">
                                <article v-for="entry in fieldGuides" :key="entry.key" class="explain-field">
                                    <h4>{{ entry.help?.title || entry.label }}</h4>
                                    <div v-if="entry.help?.body" class="explain-help-body" v-html="entry.help.body"></div>
                                    <p v-else class="explain-field-fallback">{{ entry.context }}</p>
                                    <p v-if="entry.help?.objectdesc" class="explain-object-description">
                                        {{ entry.help.objectdesc }}
                                    </p>
                                    <p v-if="entry.help?.body && entry.context" class="explain-field-context">
                                        {{ entry.context }}
                                    </p>
                                </article>
                            </div>
                        </section>

                        <section
                            v-for="guideSection in guide.sections || []"
                            :key="guideSection.title"
                            class="explain-guide-section"
                        >
                            <h3>{{ guideSection.title }}</h3>
                            <dl class="explain-list">
                                <div v-for="guideItem in guideSection.items" :key="guideItem.label" class="explain-list-item">
                                    <dt>{{ guideItem.label }}</dt>
                                    <dd>{{ guideItem.description }}</dd>
                                </div>
                            </dl>
                        </section>

                        <p v-if="loadingHelp" class="explain-loading" role="status">Loading field help...</p>
                    </div>

                    <footer class="explain-page-footer">
                        <button type="button" class="explain-done-button" @click="closeGuide">Got it</button>
                    </footer>
                </section>
            </div>
        </Teleport>
    </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useStore } from 'vuex';
import pageGuides from './explainPageGuides';

const props = defineProps({
    pageKey: {
        type: String,
        required: true,
    },
});

const store = useStore();
const isOpen = ref(false);
const triggerButton = ref(null);
const closeButton = ref(null);
const guide = computed(() => pageGuides[props.pageKey] || {
    title: 'Page guide',
    intro: 'Review the labels and lists on this page.',
    fields: [],
    sections: [],
});
const fieldGuides = computed(() => (guide.value.fields || []).map((field) => ({
    ...field,
    help: store.getters.getHelpTextByKey(field.key),
})));
const loadingHelp = computed(() => store.getters.stHelpTextLoading);

watch(isOpen, async (open) => {
    if (open) {
        await nextTick();
        closeButton.value?.focus();
    } else {
        triggerButton.value?.focus();
    }
});

async function openGuide() {
    isOpen.value = true;
    const hasFields = (guide.value.fields || []).length > 0;
    if (hasFields && Object.keys(store.getters.stHelpTextData || {}).length === 0) {
        try {
            await store.dispatch('fetchHelpText');
        } catch (error) {
            console.error('Unable to load page help text:', error);
        }
    }
}

function closeGuide() {
    isOpen.value = false;
}
</script>

<style scoped>
.explain-page-control {
    display: flex;
    justify-content: flex-end;
    margin: 0 0 12px;
}

.explain-page-trigger,
.explain-done-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 40px;
    padding: 8px 14px;
    border: 1px solid #287b79;
    border-radius: 4px;
    background: #fff;
    color: #185c5b;
    font: inherit;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 160ms ease, color 160ms ease, transform 160ms ease;
}

.explain-page-trigger:hover,
.explain-page-trigger:focus-visible {
    background: #eaf5f3;
    color: #123f3e;
}

.explain-page-trigger .material-icons,
.explain-page-close .material-icons {
    font-size: 20px;
}

.explain-page-backdrop {
    position: fixed;
    z-index: 2100;
    inset: 0;
    display: grid;
    place-items: center;
    padding: 20px;
    background: rgb(15 29 39 / 62%);
    animation: explain-fade-in 160ms ease-out;
}

.explain-page-dialog {
    display: flex;
    flex-direction: column;
    width: min(880px, 100%);
    max-height: min(88vh, 900px);
    overflow: hidden;
    border: 1px solid #d6e0e3;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 24px 72px rgb(8 25 34 / 28%);
    animation: explain-rise-in 180ms ease-out;
}

.explain-page-header {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 22px 26px;
    border-bottom: 1px solid #dbe4e7;
    background: #f5f9f8;
}

.explain-page-mark {
    display: grid;
    flex: 0 0 44px;
    width: 44px;
    height: 44px;
    place-items: center;
    border-radius: 6px;
    background: #dcefed;
    color: #1b6e6c;
}

.explain-page-mark .material-icons {
    font-size: 24px;
}

.explain-page-heading {
    min-width: 0;
    flex: 1;
}

.explain-page-eyebrow {
    color: #287b79;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.08em;
}

.explain-page-heading h2 {
    margin: 3px 0 0;
    color: #18313b;
    font-size: 1.35rem;
    line-height: 1.25;
}

.explain-page-close {
    display: grid;
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
    place-items: center;
    border: 1px solid #cbd8dc;
    border-radius: 50%;
    background: #fff;
    color: #354b54;
    cursor: pointer;
}

.explain-page-close:hover,
.explain-page-close:focus-visible {
    background: #eaf5f3;
    color: #185c5b;
}

.explain-page-body {
    overflow-y: auto;
    padding: 22px 26px 28px;
}

.explain-page-intro {
    margin: 0 0 22px;
    color: #3f535d;
    font-size: 1rem;
    line-height: 1.6;
}

.explain-guide-section + .explain-guide-section {
    margin-top: 26px;
    padding-top: 22px;
    border-top: 1px solid #e2e9eb;
}

.explain-guide-section h3 {
    margin: 0 0 13px;
    color: #193740;
    font-size: 1rem;
    font-weight: 700;
}

.explain-field-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 26px;
}

.explain-field {
    min-width: 0;
    padding: 12px 0;
    border-top: 1px solid #edf1f2;
}

.explain-field h4,
.explain-list-item dt {
    margin: 0 0 5px;
    color: #263e48;
    font-size: 0.9rem;
    font-weight: 700;
}

.explain-help-body,
.explain-field-fallback,
.explain-object-description,
.explain-field-context,
.explain-list-item dd {
    margin: 0;
    color: #50636c;
    font-size: 0.875rem;
    line-height: 1.55;
}

.explain-object-description,
.explain-field-context {
    margin-top: 7px;
    color: #65777e;
    font-size: 0.8rem;
}

.explain-help-body :deep(p:last-child) {
    margin-bottom: 0;
}

.explain-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px 28px;
    margin: 0;
}

.explain-list-item {
    min-width: 0;
}

.explain-loading {
    margin: 20px 0 0;
    color: #65777e;
    font-size: 0.875rem;
}

.explain-page-footer {
    display: flex;
    justify-content: flex-end;
    padding: 14px 26px;
    border-top: 1px solid #dbe4e7;
    background: #f8faf9;
}

.explain-done-button {
    min-width: 104px;
    background: #1d6967;
    color: #fff;
}

.explain-done-button:hover,
.explain-done-button:focus-visible {
    background: #155452;
    color: #fff;
}

@keyframes explain-fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
}

@keyframes explain-rise-in {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
}

@media (max-width: 640px) {
    .explain-page-backdrop {
        padding: 10px;
    }

    .explain-page-dialog {
        max-height: 92vh;
        border-radius: 6px;
    }

    .explain-page-header {
        gap: 10px;
        padding: 16px;
    }

    .explain-page-body {
        padding: 18px 16px 22px;
    }

    .explain-field-list,
    .explain-list {
        grid-template-columns: minmax(0, 1fr);
    }

    .explain-page-footer {
        padding: 12px 16px;
    }
}

@media (prefers-reduced-motion: reduce) {
    .explain-page-backdrop,
    .explain-page-dialog {
        animation: none;
    }

    .explain-page-trigger,
    .explain-done-button {
        transition: none;
    }
}
</style>